import Delaunator from "delaunator";

import {t} from "../../digitization/i18n/index.js";
// Automatisk udgravnings-/påfyldningsvolumen ("graverapport") fra en punktsky - JS-port af tools/compute_cut_fill_volume.py
// (Potree_local). Ingen manuel digitalisering: skyen rasteres til en højdemodel, den oprindelige terrænflade rekonstrueres, og
// de to største sammenhængende områder under/over den er udgravningen hhv. påfyldningen. Ren beregning (typed arrays, ingen
// THREE/DOM), så den kører i en worker (workers/cutFillWorker.js) og kan testes direkte (cutFillAlgorithm.test.js).
//
//  1. Højdemodel: median-z pr. celle (median, ikke minimum: en tyndt dækket celle kan ellers låse på ét punkt på en skrånende væg).
//  2. Oprindeligt terræn: interpoleret (lineær Delaunay) fra jord-klassificerede punkter (ASPRS klasse 2, renset for lokale
//     afvigere), ellers fra den scannede flades yderrand.
//  3. Celler under referencen er UDGRAVET (cut), celler over er PÅFYLDT (fill); for hver er det største sammenhængende område
//     "gruben"/"dyngen". Volumen = sum(|reference - overflade|) * celleareal.
//
// Punkter er i skyens lokale koordinatramme (verdenskoordinater minus oktræets offset), som Float32 - samme ramme som
// CutFillVolume.js (visning) og den gamle Python-fil forventer. Klasse 7 (støj) droppes før noget andet.
//
// Resultatet har samme form som cut_fill_volume.json ({cut, fill}) plus `meta` (parametre/oplysninger) - en senere jobmanager
// kan skrive samme fil, og klienten viser den, hvis den findes. `preview` (topbillede i punkternes farver) er IKKE en del af
// JSON'en.

export const ALGORITHM_VERSION = 1;

export const DEFAULTS = {
	cell: 0.3,          // m
	minDepth: 0.03,     // m: mindste dybde/højde, der tæller som udgravning/påfyldning
	minPoints: 5,       // punkter pr. celle før cellen stoles på
	minGroundPoints: 5, // ...og for jord-kontrolpunkter (kan sænkes på grove niveauer, hvor kun få punkter pr. celle er jord)
	windowM: 1.5,       // m: nabolag til afviser-filteret på jord-kontrolpunkter
	thresholdM: 0.3,    // m: største afvigelse fra nabolagets median
	maxCells: 16000000, // beskytter hukommelsen: større net får automatisk større celle
	previewMax: 1400,   // px: topbilledets længste side
};

const NEIGHBORS8 = [[-1, -1], [-1, 0], [-1, 1], [0, -1], [0, 1], [1, -1], [1, 0], [1, 1]];

// Pythons round() runder halve til lige tal
function pyRound(x){
	const f = Math.floor(x);
	const d = x - f;
	if(d < 0.5) return f;
	if(d > 0.5) return f + 1;
	return f % 2 === 0 ? f : f + 1;
}

// ---- punkter -> celler ---------------------------------------------------------------------------------------------------

// Median-z pr. celle. Pythons median_pos = start + count // 2 (den øvre median ved lige antal).
function binMedian(x, y, z, n, x0, y0, cell, nx, ny){
	const cells = nx * ny;
	const dem = new Float32Array(cells).fill(NaN);
	const counts = new Int32Array(cells);
	if(n === 0) return {dem, counts};

	const index = new Int32Array(n);
	for(let i = 0; i < n; i++){
		let ix = Math.trunc((x[i] - x0) / cell);
		let iy = Math.trunc((y[i] - y0) / cell);
		if(ix < 0) ix = 0; else if(ix > nx - 1) ix = nx - 1;
		if(iy < 0) iy = 0; else if(iy > ny - 1) iy = ny - 1;
		const flat = iy * nx + ix;
		index[i] = flat;
		counts[flat]++;
	}

	const start = new Int32Array(cells + 1);
	for(let c = 0; c < cells; c++) start[c + 1] = start[c] + counts[c];

	const fillPos = start.slice(0, cells);
	const sorted = new Float32Array(n);
	for(let i = 0; i < n; i++) sorted[fillPos[index[i]]++] = z[i];

	for(let c = 0; c < cells; c++){
		const k = counts[c];
		if(k === 0) continue;
		const a = start[c];
		if(k === 1){
			dem[c] = sorted[a];
		}else{
			const cellZ = sorted.subarray(a, a + k);
			cellZ.sort();
			dem[c] = cellZ[k >> 1];
		}
	}
	return {dem, counts};
}

// ---- raster-hjælpere ---------------------------------------------------------------------------------------------------------

// Sammenhængende områder (8-forbindelse). Returnerer {labels: Int32Array (0 = baggrund, 1..n), sizes: [0, s1, s2, ...]}
function labelComponents(mask, nx, ny){
	const labels = new Int32Array(nx * ny);
	const sizes = [0];
	const stack = new Int32Array(nx * ny);
	let n = 0;
	for(let start = 0; start < mask.length; start++){
		if(!mask[start] || labels[start]) continue;
		n++;
		let size = 0;
		let top = 0;
		stack[top++] = start;
		labels[start] = n;
		while(top > 0){
			const cur = stack[--top];
			size++;
			const r = (cur / nx) | 0;
			const c = cur - r * nx;
			for(let k = 0; k < 8; k++){
				const rr = r + NEIGHBORS8[k][0];
				const cc = c + NEIGHBORS8[k][1];
				if(rr < 0 || rr >= ny || cc < 0 || cc >= nx) continue;
				const nb = rr * nx + cc;
				if(mask[nb] && !labels[nb]){
					labels[nb] = n;
					stack[top++] = nb;
				}
			}
		}
		sizes.push(size);
	}
	return {labels, sizes, count: n};
}

function largestComponent(mask, nx, ny){
	const {labels, sizes, count} = labelComponents(mask, nx, ny);
	const out = new Uint8Array(mask.length);
	if(count === 0) return out;
	let best = 1;
	for(let l = 2; l <= count; l++) if(sizes[l] > sizes[best]) best = l;
	for(let i = 0; i < labels.length; i++) if(labels[i] === best) out[i] = 1;
	return out;
}

// scipy.ndimage.binary_fill_holes (4-forbindelse for baggrunden): baggrund, der ikke kan nås fra kanten, er huller.
function fillHoles(mask, nx, ny){
	const outside = new Uint8Array(nx * ny);
	const stack = new Int32Array(nx * ny);
	let top = 0;
	const push = (r, c) => {
		const i = r * nx + c;
		if(!mask[i] && !outside[i]){ outside[i] = 1; stack[top++] = i; }
	};
	for(let c = 0; c < nx; c++){ push(0, c); push(ny - 1, c); }
	for(let r = 0; r < ny; r++){ push(r, 0); push(r, nx - 1); }
	while(top > 0){
		const cur = stack[--top];
		const r = (cur / nx) | 0;
		const c = cur - r * nx;
		if(r > 0) push(r - 1, c);
		if(r < ny - 1) push(r + 1, c);
		if(c > 0) push(r, c - 1);
		if(c < nx - 1) push(r, c + 1);
	}
	const out = new Uint8Array(nx * ny);
	for(let i = 0; i < out.length; i++) out[i] = outside[i] ? 0 : 1;
	return out;
}

// Nærmeste "kilde"-celle for hver celle (multi-kilde BFS, 8-forbindelse) - erstatter scipys distance_transform_edt(return_indices).
// Returnerer Int32Array med indeks på nærmeste kilde (-1 hvor ingen kilde findes).
function nearestSource(isSource, nx, ny){
	const nearest = new Int32Array(nx * ny).fill(-1);
	let queue = new Int32Array(nx * ny);
	let head = 0, tail = 0;
	for(let i = 0; i < isSource.length; i++){
		if(isSource[i]){ nearest[i] = i; queue[tail++] = i; }
	}
	while(head < tail){
		const cur = queue[head++];
		const r = (cur / nx) | 0;
		const c = cur - r * nx;
		for(let k = 0; k < 8; k++){
			const rr = r + NEIGHBORS8[k][0];
			const cc = c + NEIGHBORS8[k][1];
			if(rr < 0 || rr >= ny || cc < 0 || cc >= nx) continue;
			const nb = rr * nx + cc;
			if(nearest[nb] === -1){
				nearest[nb] = nearest[cur];
				queue[tail++] = nb;
			}
		}
	}
	return nearest;
}

// Små no-data-huller INDE i den scannede flade fyldes med nærmeste gyldige nabo; den (evt. enorme) tomme flade uden for en scanning,
// der kun dækker en del af sin bounding box (fx en grøftekorridor), røres ikke.
function closeGaps(dem, valid, nx, ny){
	const {labels, sizes, count} = labelComponents(valid, nx, ny);
	if(count === 0) return {dem, footprint: valid, filled: 0};

	let best = 1;
	for(let l = 2; l <= count; l++) if(sizes[l] > sizes[best]) best = l;
	const main = new Uint8Array(valid.length);
	for(let i = 0; i < labels.length; i++) if(labels[i] === best) main[i] = 1;

	const footprint = fillHoles(main, nx, ny);
	let gaps = 0;
	for(let i = 0; i < footprint.length; i++) if(footprint[i] && !valid[i]) gaps++;
	if(gaps > 0){
		const nearest = nearestSource(valid, nx, ny);
		for(let i = 0; i < footprint.length; i++){
			if(footprint[i] && !valid[i] && nearest[i] >= 0) dem[i] = dem[nearest[i]];
		}
	}
	return {dem, footprint, filled: gaps};
}

function findRimCells(footprint, nx, ny){
	const rim = new Uint8Array(nx * ny);
	for(let r = 0; r < ny; r++){
		for(let c = 0; c < nx; c++){
			const i = r * nx + c;
			if(!footprint[i]) continue;
			let edge = r === 0 || r === ny - 1 || c === 0 || c === nx - 1;
			if(!edge){
				for(let k = 0; k < 8; k++){
					if(!footprint[(r + NEIGHBORS8[k][0]) * nx + (c + NEIGHBORS8[k][1])]){ edge = true; break; }
				}
			}
			rim[i] = edge ? 1 : 0;
		}
	}
	return rim;
}

// Celler, hvis højde afviger for meget fra deres eget nabolags median (scipy median_filter, mode 'reflect').
function rejectLocalOutliers(dem, valid, nx, ny, cell, windowM, thresholdM){
	const size = Math.max(3, pyRound(windowM / cell) | 1);
	let any = false;
	for(let i = 0; i < valid.length; i++) if(valid[i]){ any = true; break; }
	if(!any) return valid;

	const values = [];
	for(let i = 0; i < valid.length; i++) if(valid[i]) values.push(dem[i]);
	values.sort((a, b) => a - b);
	const m = values.length;
	const fallback = m % 2 ? values[(m - 1) / 2] : (values[m / 2 - 1] + values[m / 2]) / 2;

	const filled = new Float32Array(nx * ny);
	for(let i = 0; i < filled.length; i++) filled[i] = valid[i] ? dem[i] : fallback;

	const half = (size - 1) / 2;
	const reflect = (v, n) => (v < 0 ? -v - 1 : (v >= n ? 2 * n - v - 1 : v));
	const window = new Float32Array(size * size);
	const rank = (size * size) >> 1;
	const out = new Uint8Array(nx * ny);

	for(let r = 0; r < ny; r++){
		for(let c = 0; c < nx; c++){
			const i = r * nx + c;
			if(!valid[i]) continue;
			let k = 0;
			for(let dr = -half; dr <= half; dr++){
				const rr = reflect(r + dr, ny);
				for(let dc = -half; dc <= half; dc++){
					window[k++] = filled[rr * nx + reflect(c + dc, nx)];
				}
			}
			window.sort();
			if(Math.abs(dem[i] - window[rank]) < thresholdM) out[i] = 1;
		}
	}
	return out;
}

// Lineær Delaunay-interpolation af kontrolpunkter (celle-centre) til hele nettet; uden for det konvekse hylster nærmeste kontrolpunkt.
function interpolateFromControls(controlCells, heights, x0, y0, cell, nx, ny){
	const m = controlCells.length;
	const reference = new Float32Array(nx * ny).fill(NaN);

	const coords = new Float64Array(m * 2);
	for(let k = 0; k < m; k++){
		const c = controlCells[k] % nx;
		const r = (controlCells[k] - c) / nx;
		coords[2 * k] = x0 + (c + 0.5) * cell;
		coords[2 * k + 1] = y0 + (r + 0.5) * cell;
	}

	if(m >= 3){
		const delaunay = new Delaunator(coords);
		const tri = delaunay.triangles;
		for(let t = 0; t < tri.length; t += 3){
			const a = tri[t], b = tri[t + 1], c = tri[t + 2];
			const ax = coords[2 * a], ay = coords[2 * a + 1];
			const bx = coords[2 * b], by = coords[2 * b + 1];
			const cx = coords[2 * c], cy = coords[2 * c + 1];
			const det = (by - cy) * (ax - cx) + (cx - bx) * (ay - cy);
			if(Math.abs(det) < 1e-12) continue;

			const minC = Math.max(0, Math.floor((Math.min(ax, bx, cx) - x0) / cell - 0.5));
			const maxC = Math.min(nx - 1, Math.ceil((Math.max(ax, bx, cx) - x0) / cell - 0.5));
			const minR = Math.max(0, Math.floor((Math.min(ay, by, cy) - y0) / cell - 0.5));
			const maxR = Math.min(ny - 1, Math.ceil((Math.max(ay, by, cy) - y0) / cell - 0.5));
			const za = heights[a], zb = heights[b], zc = heights[c];

			for(let r = minR; r <= maxR; r++){
				const py = y0 + (r + 0.5) * cell;
				for(let col = minC; col <= maxC; col++){
					const px = x0 + (col + 0.5) * cell;
					const l1 = ((by - cy) * (px - cx) + (cx - bx) * (py - cy)) / det;
					const l2 = ((cy - ay) * (px - cx) + (ax - cx) * (py - cy)) / det;
					const l3 = 1 - l1 - l2;
					const eps = -1e-9;
					if(l1 >= eps && l2 >= eps && l3 >= eps){
						reference[r * nx + col] = l1 * za + l2 * zb + l3 * zc;
					}
				}
			}
		}
	}

	// uden for hylsteret: nærmeste kontrolpunkt
	let missing = 0;
	for(let i = 0; i < reference.length; i++) if(Number.isNaN(reference[i])) missing++;
	if(missing > 0){
		const isControl = new Uint8Array(nx * ny);
		const controlHeight = new Float32Array(nx * ny);
		for(let k = 0; k < m; k++){ isControl[controlCells[k]] = 1; controlHeight[controlCells[k]] = heights[k]; }
		const nearest = nearestSource(isControl, nx, ny);
		for(let i = 0; i < reference.length; i++){
			if(Number.isNaN(reference[i]) && nearest[i] >= 0) reference[i] = controlHeight[nearest[i]];
		}
	}
	return reference;
}

function buildReferenceSurface(dem, footprint, nx, ny, x0, y0, cell, groundDem, groundCounts, p){
	let groundValid = null;
	if(groundDem){
		groundValid = new Uint8Array(nx * ny);
		for(let i = 0; i < groundValid.length; i++) groundValid[i] = groundCounts[i] >= p.minGroundPoints ? 1 : 0;
		groundValid = rejectLocalOutliers(groundDem, groundValid, nx, ny, cell, p.windowM, p.thresholdM);
	}

	let groundCount = 0;
	if(groundValid) for(let i = 0; i < groundValid.length; i++) groundCount += groundValid[i];

	if(groundValid && groundCount >= 20){
		const cells = [];
		const heights = [];
		for(let i = 0; i < groundValid.length; i++){
			if(groundValid[i]){ cells.push(i); heights.push(groundDem[i]); }
		}
		return {reference: interpolateFromControls(cells, heights, x0, y0, cell, nx, ny), method: "ground", controlPoints: cells.length};
	}

	const rim = findRimCells(footprint, nx, ny);
	const cells = [];
	const heights = [];
	for(let i = 0; i < rim.length; i++) if(rim[i]){ cells.push(i); heights.push(dem[i]); }

	const sorted = heights.slice().sort((a, b) => a - b);
	const median = (arr) => (arr.length % 2 ? arr[(arr.length - 1) / 2] : (arr[arr.length / 2 - 1] + arr[arr.length / 2]) / 2);
	const med = median(sorted);
	const deviations = heights.map(h => Math.abs(h - med)).sort((a, b) => a - b);
	const mad = median(deviations);
	const threshold = Math.max(3 * 1.4826 * mad, 0.15);

	let keepCells = [], keepHeights = [];
	for(let k = 0; k < heights.length; k++){
		if(Math.abs(heights[k] - med) < threshold){ keepCells.push(cells[k]); keepHeights.push(heights[k]); }
	}
	if(keepCells.length < 4){ keepCells = cells; keepHeights = heights; }

	return {reference: interpolateFromControls(keepCells, keepHeights, x0, y0, cell, nx, ny), method: "rim", controlPoints: keepCells.length};
}

// ---- områder -----------------------------------------------------------------------------------------------------------------

function convexHull(points){
	const pts = points.slice().sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));
	if(pts.length < 3) return pts;
	const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
	const lower = [];
	for(const p of pts){
		while(lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
		lower.push(p);
	}
	const upper = [];
	for(let i = pts.length - 1; i >= 0; i--){
		const p = pts[i];
		while(upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
		upper.push(p);
	}
	lower.pop();
	upper.pop();
	return lower.concat(upper);
}

// Konvekst hylster af områdets celle-centre, forenklet (mindste trekant-areal fjernes først) til højst `maxVertices` hjørner -
// polygon-clip-volumen i vieweren understøtter ikke flere.
function computeHull(mask, nx, ny, x0, y0, cell, maxVertices = 8){
	const points = [];
	for(let r = 0; r < ny; r++){
		for(let c = 0; c < nx; c++){
			if(mask[r * nx + c]) points.push([x0 + (c + 0.5) * cell, y0 + (r + 0.5) * cell]);
		}
	}
	if(points.length < 3) return [];

	const polygon = convexHull(points);
	while(polygon.length > maxVertices){
		const n = polygon.length;
		let bestIndex = 0, bestArea = Infinity;
		for(let i = 0; i < n; i++){
			const a = polygon[(i + n - 1) % n], b = polygon[i], c = polygon[(i + 1) % n];
			const area = Math.abs((b[0] - a[0]) * (c[1] - a[1]) - (c[0] - a[0]) * (b[1] - a[1])) / 2;
			if(area < bestArea){ bestArea = area; bestIndex = i; }
		}
		polygon.splice(bestIndex, 1);
	}
	return polygon.map(([x, y]) => [x, y]);
}

function emptyRegion(kind, cell){
	return {
		kind, volume_m3: 0, area_m2: 0, max_depth_m: 0, centroid: [0, 0, 0], hull: [],
		grid: {nx: 0, ny: 0, x0: 0, y0: 0, cell, surface: [], reference: [], mask: []},
	};
}

const round4 = (v) => Math.round(v * 1e4) / 1e4;

function extractRegion(kind, dem, reference, mask, nx, ny, x0, y0, cell){
	let cellsInMask = 0;
	for(let i = 0; i < mask.length; i++) cellsInMask += mask[i];
	if(cellsInMask === 0) return emptyRegion(kind, cell);

	let depthSum = 0, maxDepth = 0;
	let r0 = ny, r1 = -1, c0 = nx, c1 = -1;
	let sumC = 0, sumR = 0, sumZ = 0;
	for(let r = 0; r < ny; r++){
		for(let c = 0; c < nx; c++){
			const i = r * nx + c;
			if(!mask[i]) continue;
			let depth = Math.abs(reference[i] - dem[i]);
			if(Number.isNaN(depth)) depth = 0;
			depthSum += depth;
			if(depth > maxDepth) maxDepth = depth;
			if(r < r0) r0 = r;
			if(r > r1) r1 = r;
			if(c < c0) c0 = c;
			if(c > c1) c1 = c;
			sumC += c; sumR += r; sumZ += dem[i];
		}
	}

	const cellArea = cell * cell;
	r1 += 1; c1 += 1;
	// lille margen, så vægge/låg har gyldige naboer at lukke mod
	r0 = Math.max(0, r0 - 1); c0 = Math.max(0, c0 - 1);
	r1 = Math.min(ny, r1 + 1); c1 = Math.min(nx, c1 + 1);

	const subNx = c1 - c0, subNy = r1 - r0;
	const surface = new Array(subNx * subNy), ref = new Array(subNx * subNy), subMask = new Array(subNx * subNy);
	for(let r = 0; r < subNy; r++){
		for(let c = 0; c < subNx; c++){
			const i = (r0 + r) * nx + (c0 + c);
			const k = r * subNx + c;
			surface[k] = Number.isNaN(dem[i]) ? 0 : round4(dem[i]);
			ref[k] = Number.isNaN(reference[i]) ? 0 : round4(reference[i]);
			subMask[k] = mask[i] ? 1 : 0;
		}
	}

	return {
		kind,
		volume_m3: depthSum * cellArea,
		area_m2: cellsInMask * cellArea,
		max_depth_m: maxDepth,
		centroid: [x0 + (sumC / cellsInMask) * cell, y0 + (sumR / cellsInMask) * cell, sumZ / cellsInMask],
		hull: computeHull(mask, nx, ny, x0, y0, cell),
		grid: {nx: subNx, ny: subNy, x0: x0 + c0 * cell, y0: y0 + r0 * cell, cell, surface, reference: ref, mask: subMask},
	};
}

// ---- topbillede (punkternes farver) ---------------------------------------------------------------------------------------

// Gennemsnitsfarve pr. billedpunkt, set oppefra (nord op). Ikke en del af JSON'en.
function buildPreview(x, y, rgb, n, noise, xMin, yMin, xMax, yMax, previewMax){
	if(!rgb) return null;
	const spanX = Math.max(xMax - xMin, 1e-6), spanY = Math.max(yMax - yMin, 1e-6);
	const pixel = Math.max(spanX, spanY) / previewMax;
	const width = Math.max(1, Math.ceil(spanX / pixel)), height = Math.max(1, Math.ceil(spanY / pixel));
	const sum = new Uint32Array(width * height * 3);
	const count = new Uint32Array(width * height);
	for(let i = 0; i < n; i++){
		if(noise && noise[i]) continue;
		const px = Math.min(width - 1, Math.max(0, Math.trunc((x[i] - xMin) / pixel)));
		const py = Math.min(height - 1, Math.max(0, Math.trunc((yMax - y[i]) / pixel)));
		const k = py * width + px;
		sum[k * 3] += rgb[i * 3];
		sum[k * 3 + 1] += rgb[i * 3 + 1];
		sum[k * 3 + 2] += rgb[i * 3 + 2];
		count[k]++;
	}
	const out = new Uint8Array(width * height * 3).fill(255);
	for(let k = 0; k < count.length; k++){
		if(count[k] === 0) continue;
		out[k * 3] = Math.round(sum[k * 3] / count[k]);
		out[k * 3 + 1] = Math.round(sum[k * 3 + 1] / count[k]);
		out[k * 3 + 2] = Math.round(sum[k * 3 + 2] / count[k]);
	}
	return {width, height, rgb: out, x0: xMin, yTop: yMax, pixel};
}

// ---- hovedfunktion --------------------------------------------------------------------------------------------------------------

// points: {x, y, z: Float32Array (lokale koordinater), cls: Uint8Array|null, rgb: Uint8Array|null (3 pr. punkt), count}
// params: se DEFAULTS. progress(stage: string).
export function computeCutFill(points, params = {}, progress = () => {}){
	const p = Object.assign({}, DEFAULTS, params);
	const {x, y, z, cls, rgb} = points;
	const total = points.count !== undefined ? points.count : x.length;
	if(total === 0) throw new Error(t("Punktskyen har ingen punkter i det valgte niveau."));

	// klasse 7 (støj) droppes før al rasterering
	let noise = null, noiseCount = 0;
	if(cls){
		noise = new Uint8Array(total);
		for(let i = 0; i < total; i++){ if(cls[i] === 7){ noise[i] = 1; noiseCount++; } }
	}
	let n = total;
	let xs = x, ys = y, zs = z, clsKept = cls;
	if(noiseCount > 0){
		n = total - noiseCount;
		xs = new Float32Array(n); ys = new Float32Array(n); zs = new Float32Array(n);
		clsKept = new Uint8Array(n);
		let k = 0;
		for(let i = 0; i < total; i++){
			if(noise[i]) continue;
			xs[k] = x[i]; ys[k] = y[i]; zs[k] = z[i]; clsKept[k] = cls[i]; k++;
		}
	}
	if(n === 0) throw new Error(t("Punktskyen har kun støjpunkter."));

	progress(t("Beregner højdemodel ..."));
	let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
	for(let i = 0; i < n; i++){
		if(xs[i] < xMin) xMin = xs[i]; if(xs[i] > xMax) xMax = xs[i];
		if(ys[i] < yMin) yMin = ys[i]; if(ys[i] > yMax) yMax = ys[i];
	}

	let cell = p.cell;
	let nx = Math.ceil((xMax - xMin) / cell) + 1;
	let ny = Math.ceil((yMax - yMin) / cell) + 1;
	while(nx * ny > p.maxCells){
		cell *= 1.5;
		nx = Math.ceil((xMax - xMin) / cell) + 1;
		ny = Math.ceil((yMax - yMin) / cell) + 1;
	}
	const x0 = xMin, y0 = yMin;

	const {dem, counts} = binMedian(xs, ys, zs, n, x0, y0, cell, nx, ny);

	let groundDem = null, groundCounts = null, groundPoints = 0;
	if(clsKept){
		for(let i = 0; i < n; i++) if(clsKept[i] === 2) groundPoints++;
		if(groundPoints > 0){
			const gx = new Float32Array(groundPoints), gy = new Float32Array(groundPoints), gz = new Float32Array(groundPoints);
			let k = 0;
			for(let i = 0; i < n; i++){
				if(clsKept[i] === 2){ gx[k] = xs[i]; gy[k] = ys[i]; gz[k] = zs[i]; k++; }
			}
			const g = binMedian(gx, gy, gz, groundPoints, x0, y0, cell, nx, ny);
			groundDem = g.dem; groundCounts = g.counts;
		}
	}

	const preview = buildPreview(x, y, rgb, total, noise, xMin, yMin, xMax, yMax, p.previewMax);

	const valid = new Uint8Array(nx * ny);
	let validCount = 0;
	for(let i = 0; i < valid.length; i++){ if(counts[i] >= p.minPoints){ valid[i] = 1; validCount++; } }
	if(validCount === 0){
		throw new Error(t("Ingen celler har nok punkter (minimum {0} pr. {1} m celle) - punktskyen er for tynd i det valgte niveau.", p.minPoints, cell.toFixed(2)));
	}

	// en meget tynd sky giver mange ugyldige celler og dermed en usikker (typisk for lav) udgravning
	const warnings = [];
	if(n / validCount < 2 * p.minPoints){
		warnings.push(t("Punktskyen er meget tynd (i gennemsnit {0} punkter pr. celle) - resultatet er usikkert.", (n / validCount).toFixed(1)));
	}

	progress(t("Lukker huller i overfladen ..."));
	const closed = closeGaps(dem, valid, nx, ny);
	const footprint = closed.footprint;

	progress(t("Rekonstruerer det oprindelige terræn ..."));
	const {reference, method, controlPoints} = buildReferenceSurface(dem, footprint, nx, ny, x0, y0, cell, groundDem, groundCounts, p);

	progress(t("Finder udgravning og påfyldning ..."));
	const cutMask = new Uint8Array(nx * ny), fillMask = new Uint8Array(nx * ny);
	for(let i = 0; i < cutMask.length; i++){
		if(!footprint[i]) continue;
		const d = reference[i] - dem[i];
		if(Number.isNaN(d)) continue;
		if(d > p.minDepth) cutMask[i] = 1;
		else if(-d > p.minDepth) fillMask[i] = 1;
	}
	const cutMain = largestComponent(cutMask, nx, ny);
	const fillMain = largestComponent(fillMask, nx, ny);

	const cut = extractRegion("cut", dem, reference, cutMain, nx, ny, x0, y0, cell);
	const fill = extractRegion("fill", dem, reference, fillMain, nx, ny, x0, y0, cell);

	return {
		version: ALGORITHM_VERSION,
		cut,
		fill,
		meta: {
			algorithmVersion: ALGORITHM_VERSION,
			cell,
			requestedCell: p.cell,
			minDepth: p.minDepth,
			minPoints: p.minPoints,
			points: n,
			noisePoints: noiseCount,
			groundPoints,
			warnings,
			referenceMethod: method,
			controlPoints,
			cells: {nx, ny, valid: validCount, footprint: footprintCount(footprint), gapsFilled: closed.filled},
			extent: {xMin, xMax, yMin, yMax},
		},
		preview,
	};
}

function footprintCount(footprint){
	let n = 0;
	for(let i = 0; i < footprint.length; i++) n += footprint[i];
	return n;
}
